import React, { createContext, useContext, type ReactNode } from 'react';

import { CreditsProvider, useCreditsState } from '@atlassian/rovo-credits/credits-provider';
import type { CreditsErrorHandler } from '@atlassian/rovo-credits/credits-provider/types';

import { useCloudId } from '@townsquare/collab-context';
import { logMessage } from '@townsquare/logging';
import { fg } from '@townsquare/stat-sig/gate';

import { useRovoEntitlement } from './entitlement-resource';

const reportCreditsError: CreditsErrorHandler = (error, context) => {
	logMessage('Rovo credits session error', 'warning', { context, error });
};

const RovoCreditsDepletedContext = createContext(false);

/**
 * This check only tells us if the user has consumed all of their credits.
 * Checking this alone is not enough to determine whether the user is able
 * to use Rovo, as they may not have permissions or provisioning.
 * Use useRovoEntitlement to check for Rovo access
 *
 * Will be false until we get a response from CreditsProvider
 */
export const useIsRovoCreditsDepleted = (): boolean => useContext(RovoCreditsDepletedContext);

type RovoCreditsProviderProps = {
	children: ReactNode;
};

const RovoCreditsDepletedProvider = ({ children }: RovoCreditsProviderProps) => {
	const { bannerState } = useCreditsState();

	return (
		<RovoCreditsDepletedContext.Provider
			value={bannerState?.balanceState === 'depleted' && bannerState.depletionBehavior === 'block'}
		>
			{children}
		</RovoCreditsDepletedContext.Provider>
	);
};

export const RovoCreditsProvider = ({ children }: RovoCreditsProviderProps): React.JSX.Element => {
	const { data } = useRovoEntitlement();
	const cloudId = useCloudId();

	if (!cloudId || !fg('townsquare_enable_rovo_home_credits')) {
		return <>{children}</>;
	}

	return (
		<CreditsProvider
			product="home"
			cloudId={cloudId}
			isRovoEnabled={Boolean(data?.isRovoLLMEnabled)}
			onError={reportCreditsError}
		>
			<RovoCreditsDepletedProvider>{children}</RovoCreditsDepletedProvider>
		</CreditsProvider>
	);
};
